import cls from "classnames";
import { useRef, useState, useMemo } from "react";
import Glider from 'react-glider';
import 'glider-js/glider.min.css';

import { MultiCurrency } from "@qoohoo/common/utils/currency";
import { formatStringWithRichText } from "@qoohoo/common/utils/string";
import UserAvatar from "@qoohoo/common/components/UserAvatar";
import useScreenSize from "@qoohoo/common/hooks/useScreenSize";

import usePreferencesStore from "store/preferencesStore";
import useEntityStore from "store/entityStore";

import { QOOHOO_WEBSITE_DOMAIN } from "constants/env";
import ClockIcon from "components/icons/ClockIcon";
import GoogleMeetIcon from "components/icons/GoogleMeetIcon";
import BackChevronIcon from "components/icons/BackChevronIcon";

export default function MeetingDetails() {
  const entity = useEntityStore((state) => state.entity);
  const hasMedia = entity.data.media.length > 0; 

  const formattedDescription = formatStringWithRichText(entity.data.description);

  return (
    <div className="flex flex-col gap-6 sm:gap-8 w-full">
      { hasMedia && <MediaCarousel media={entity.data.media}/>}
      <div className={cls("flex flex-col gap-6 sm:gap-8 w-full px-5 pb-5 sm:px-8 md:p-0", !hasMedia && "pt-5" )}>
        <TitleAndPrice />
        <div className="text-sm" dangerouslySetInnerHTML={{__html: formattedDescription}}>
        </div>
        <UserInfo />
      </div>
    </div>
  );
}

function MediaCarousel({ media }) {
  const preferences = usePreferencesStore((state) => state.preferences);
  const [gliderRef, setGliderRef] = useState(null);
  const leftArrowRef = useRef(null);
  const rightArrowRef = useRef(null);

  const showSwiperControls = media.length > 1;

  return (
    <div className="w-full relative">
      {showSwiperControls && (
        <div
          className="absolute left-6 top-1/2 -translate-y-1/2 -translate-x-1/2 bg-[#ffffff] bg-opacity-80 border-[#e8e8e8] border-opacity-30  z-[2] h-8 w-8 rounded-full flex items-center justify-center cursor-pointer"
          ref={leftArrowRef}
        >
          <BackChevronIcon fill={preferences.data.theme.brand_color} />
        </div>
      )}
      <Glider
        className="glider-container w-full rounded-[0.25rem] overflow-hidden"
        slidesToShow={1}
        slidesToScroll={1}
        scrollLockDelay={100}
        dragVelocity={1}
        rewind={false}
        arrows={{
          prev: leftArrowRef.current,
          next: rightArrowRef.current,
        }}
        scrollLock
        resizeLock
        hasArrows
        draggable
        ref={setGliderRef}
      >
        {media.map((img) => (
          <div
            key={img.uid}
            className="max-h-[11.25rem] !w-full sm:max-h-60"
          >
            <img src={img.url} className="w-full object-cover" />
          </div>
        ))}
      </Glider>
      {showSwiperControls && (
        <div
          className="absolute right-6 top-1/2 rotate-180 -translate-y-1/2 translate-x-1/2 bg-[#ffffff] bg-opacity-80 border-[#e8e8e8] z-[2] h-8 w-8 rounded-full flex items-center justify-center cursor-pointer"
          ref={rightArrowRef}
        >
          <BackChevronIcon fill={preferences.data.theme.brand_color} />
        </div>
      )}
    </div>
  );
}

function TitleAndPrice() {
  const entity = useEntityStore((state) => state.entity);

  function convertDurationToReadableTime(durationInMinutes) {
    let minutes = +durationInMinutes;
    const hours = Math.floor(minutes / 60);
    minutes = minutes % 60;

    let text = "";

    if (minutes) {
      text = `${minutes} min`;
    }

    if (hours) {
      text = `${hours} hr ${text}`;
    }

    return text;
  }

  const screenSize = useScreenSize();

  const titleFontSize = useMemo(() => {
    let fontSize;

    const isSmallScreen = ["xs"].indexOf(screenSize) > -1;
    const textLength = entity.data.name.length; 

    if(isSmallScreen) {
      if(textLength > 50) {
        fontSize = "1.375rem";
      } else {
        fontSize = "1.5rem";
      }
    } else {
      if(textLength > 66) {
        fontSize = "1.625rem";
      } else {
        fontSize = "1.75rem";
      }
    }

    return fontSize;
  }, [screenSize, entity.data.name])

  return (
    <div className="flex flex-col w-full">
      <div className="w-fit pb-4 relative">
        <span className="line-clamp-3 text-ellipsis font-bold" style={{ lineHeight: 1.33, fontSize: titleFontSize}}>
          {entity.data.name}
        </span>
        <div className="w-14 h-[3px] absolute bottom-0 left-0 bg-primary"></div>
      </div>
      <div className="flex items-center gap-6 sm:gap-8 mt-4">
        <div className="flex items-center gap-2">
          <span className="text-[#6A7175] text-sm">Price</span>
          <span className="text-[#16191B] text-base sm:text-xl font-bold">
            {MultiCurrency.formatAmountWithSymbol(
              entity.data.payment.baseAmount,
              entity.data.payment.currency
            )}
          </span>
        </div>
        <div className="flex items-center gap-1 whitespace-nowrap text-[#6A7175] text-sm sm:text-base font-semibold">
          <ClockIcon className="h-4 w-4" />
          <span>
            {convertDurationToReadableTime(entity.data.meta.duration)}
          </span>
        </div>
        <div className="flex items-center gap-1 whitespace-nowrap text-[#6A7175] text-sm sm:text-base font-semibold">
          <GoogleMeetIcon className="h-4 w-4" />
          <span>Google Meet</span>
        </div>
      </div>
    </div>
  );
}

function UserInfo() {
  const entity = useEntityStore((state) => state.entity);

  return (
    <div className="flex flex-col gap-4">
      <a
        href={`${QOOHOO_WEBSITE_DOMAIN}/${entity.data.creator.username}`}
        target="_blank"
        className="flex items-center gap-2 cursor-pointer w-fit"
      >
        <div className="flex items-center justify-center bg-[#1F2429] font-bold h-7 w-7 sm:h-9 sm:w-9 overflow-hidden rounded-full">
          <UserAvatar
            avatarUrl={entity.data.creator.avatar.url}
            userName={entity.data.creator.username}
            isUserNameSetByUser={!!entity.data.creator.fullname}
            alt=""
          />
        </div>
        <div className="text-base sm:text-xl font-semibold">
          {entity.data.creator.fullname}
        </div>
      </a>
      <div className="text-sm">{entity.data.creator.bio}</div>
    </div>
  );
}
